/**
 * Payment history — `GET payments/payments/`, scoped to the caller, paginated.
 * Read-only: there is no plain payment create/edit UI here — the only write
 * this feature area exposes is "Record payment" on the invoice detail page
 * (`record_actual_payment`), which is what actually settles a balance.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Banknote } from 'lucide-react';
import { parseApiError } from '../../lib/apiError';
import { useIsMobile } from '../../lib/useIsMobile';
import { EmptyState } from '../../components/EmptyState';
import { listPayments } from '../../api/endpoints/payments';

export function PaymentListPage(): React.JSX.Element {
  const isMobile = useIsMobile();
  const [page, setPage] = useState(1);
  const [property, setProperty] = useState('');

  const query = useQuery({
    queryKey: ['payments', { page, property }],
    queryFn: () =>
      listPayments({
        page,
        property: property === '' ? undefined : Number(property),
      }),
  });

  const payments = query.data?.results ?? [];
  const hasNext = query.data?.next !== null && query.data?.next !== undefined;
  const hasPrevious = query.data?.previous !== null && query.data?.previous !== undefined;

  return (
    <section>
      <h2>Payment history</h2>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          setPage(1);
        }}
      >
        <label htmlFor="payment-property">Property id</label>
        <input
          id="payment-property"
          type="number"
          value={property}
          onChange={(event) => setProperty(event.target.value)}
        />

        <button type="submit">Apply</button>
      </form>

      {query.isPending && <p>Loading…</p>}
      {query.isError && (
        <p role="alert" className="form__error">
          {parseApiError(query.error).message}
        </p>
      )}

      {query.isSuccess && payments.length === 0 && (
        <EmptyState icon={Banknote} message="No payments found." />
      )}

      {query.isSuccess && payments.length > 0 && (
        isMobile ? (
          <ul className="record-cards">
            {payments.map((payment) => (
              <li key={payment.id} className="record-card">
                <div className="record-card__row">
                  <span className="record-card__label">Invoice #</span>
                  <span className="record-card__value">{payment.invoice_number ?? '—'}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Amount</span>
                  <span className="record-card__value">{payment.amount}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Date</span>
                  <span className="record-card__value">{payment.payment_date}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Method</span>
                  <span className="record-card__value">{payment.payment_method}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Reference</span>
                  <span className="record-card__value">{payment.transaction_reference}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Status</span>
                  <span className="record-card__value">{payment.status}</span>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Amount</th>
                <th>Date</th>
                <th>Method</th>
                <th>Reference</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((payment) => (
                <tr key={payment.id}>
                  <td>{payment.invoice_number ?? '—'}</td>
                  <td>{payment.amount}</td>
                  <td>{payment.payment_date}</td>
                  <td>{payment.payment_method}</td>
                  <td>{payment.transaction_reference}</td>
                  <td>{payment.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}

      <p>
        <button type="button" disabled={!hasPrevious} onClick={() => setPage((p) => p - 1)}>
          Previous
        </button>{' '}
        <span>Page {page}</span>{' '}
        <button type="button" disabled={!hasNext} onClick={() => setPage((p) => p + 1)}>
          Next
        </button>
      </p>
    </section>
  );
}

export default PaymentListPage;
